import React, { useMemo, useState } from 'react';
import {
  Search, Rocket, Building2, CalendarDays, BookOpen, Users, UserCog,
  Briefcase, FileText, Receipt, Mail, ShieldCheck, Settings, Lock,
  ChevronDown, LifeBuoy, Globe2,
} from 'lucide-react';

const SECTIONS = [
  {
    id: 'demarrage', icon: Rocket, title: 'Démarrage rapide',
    items: [
      { q: "Par où commencer avec OFManager ?", a: "1) Renseignez votre organisme dans Paramètres → Organisation (raison sociale, SIRET, NDA). 2) Créez vos programmes de formation. 3) Ajoutez vos entreprises clientes et apprenants. 4) Planifiez votre première session. Le tableau de bord vous guide avec la liste « À faire »." },
      { q: "Comment personnaliser mon tableau de bord ?", a: "Cliquez sur « Personnaliser » en haut du tableau de bord pour choisir les widgets affichés (CA, sessions actives, pipeline, Qualiopi…). Les périodes 7/30/90 jours et 1 an filtrent toutes les statistiques." },
      { q: "Comment configurer ma signature email ?", a: "Paramètres → Emails → Signature. Vous pouvez téléverser une image et un texte de signature qui seront ajoutés automatiquement à tous les emails envoyés depuis le CRM et la boîte email." },
    ],
  },
  {
    id: 'multiorg', icon: Building2, title: 'Multi-organisations & connexion',
    items: [
      { q: "Mon email est rattaché à plusieurs organismes, comment ça marche ?", a: "À la connexion (mot de passe, lien magique, Google ou Microsoft), si votre email existe dans plusieurs organismes, un écran vous demande de choisir celui auquel accéder. Chaque organisme dispose de son propre compte avec son rôle et ses données isolées." },
      { q: "Comment changer d'organisme sans me déconnecter ?", a: "Cliquez sur votre avatar en haut à droite : la section « Changer d'organisme » liste vos autres organismes. Un clic vous y bascule instantanément." },
      { q: "Comment être rattaché à un second organisme ?", a: "L'administrateur de l'autre organisme vous invite depuis Paramètres → Équipe avec votre email habituel. En acceptant l'invitation, un compte séparé est créé pour cet organisme — vos accès existants ne sont pas modifiés." },
      { q: "J'ai oublié mon mot de passe", a: "Sur la page de connexion, cliquez sur « Mot de passe oublié ? ». Le lien reçu par email réinitialise le mot de passe de tous vos comptes partageant cet email, puis vous propose de choisir votre organisme." },
      { q: "Comment activer la double authentification (2FA) ?", a: "Mon compte → Sécurité → Vérification en deux étapes. Scannez le QR code avec une application d'authentification (Google Authenticator, Authy…) et conservez vos codes de secours." },
    ],
  },
  {
    id: 'sessions', icon: CalendarDays, title: 'Sessions & émargements',
    items: [
      { q: "Comment créer une session de formation ?", a: "Sessions → Nouvelle session : choisissez le programme, les dates/créneaux, le formateur et le lieu (présentiel, distanciel ou mixte). Vous pouvez ensuite inscrire des apprenants individuels ou des entreprises entières." },
      { q: "Comment gérer les émargements ?", a: "Chaque session génère des feuilles d'émargement par demi-journée. Les apprenants signent électroniquement via le lien envoyé par email ou directement sur tablette. Le suivi en temps réel est visible dans Émargements." },
      { q: "Comment envoyer les convocations ?", a: "Depuis la fiche session → onglet Documents → Convocations. Elles sont générées en PDF avec vos en-têtes personnalisés et envoyées par email à chaque apprenant en un clic." },
      { q: "Comment suivre le remplissage de mes sessions ?", a: "Le widget « Remplissage 30j » du tableau de bord affiche le taux places occupées/disponibles. Sur chaque session, la jauge d'inscription indique la capacité restante." },
    ],
  },
  {
    id: 'programmes', icon: BookOpen, title: 'Programmes',
    items: [
      { q: "Comment créer un programme conforme Qualiopi ?", a: "Programmes → Nouveau : renseignez objectifs pédagogiques, prérequis, public visé, modalités d'évaluation, accessibilité handicap et tarifs. La fiche programme générée en PDF reprend automatiquement tous les champs exigés par le référentiel." },
      { q: "Puis-je générer une fiche programme avec l'IA ?", a: "Oui — dans le formulaire programme, le bouton IA rédige objectifs et contenu pédagogique à partir du titre. Relisez et ajustez avant publication." },
      { q: "Comment classer mes programmes (NSF, Formacode) ?", a: "Chaque programme accepte un code NSF et des spécialités. Le rapport NSF (Bilan Pédagogique et Financier) s'appuie sur ces codes — pensez à les renseigner." },
    ],
  },
  {
    id: 'apprenants', icon: Users, title: 'Apprenants & entreprises',
    items: [
      { q: "Comment rattacher une entreprise à son OPCO ?", a: "Fiche entreprise → section Fiscalité : saisissez le ou les codes IDCC de la convention collective. OFManager suggère automatiquement l'OPCO correspondant (bouton « ✨ Suggérer ») parmi les 11 OPCO officiels préchargés. Vous pouvez aussi le choisir manuellement." },
      { q: "Comment importer une entreprise depuis son SIRET ?", a: "Dans le formulaire entreprise, saisissez le SIRET ou le nom : les données officielles (adresse, NAF, forme juridique, IDCC, TVA intra) sont récupérées automatiquement depuis l'annuaire SIRENE." },
      { q: "Comment inscrire un apprenant à une session ?", a: "Depuis la fiche session → Inscriptions → Ajouter, ou depuis la fiche apprenant → Inscrire à une session. Les inscriptions par entreprise permettent d'ajouter plusieurs salariés d'un coup." },
      { q: "À quoi sert la vue 360° d'une entreprise ?", a: "La fiche entreprise regroupe tout : contacts, sessions suivies, devis, factures, conventions, emails échangés, questionnaires et documents. C'est le point d'entrée pour préparer un rendez-vous client." },
    ],
  },
  {
    id: 'formateurs', icon: UserCog, title: 'Formateurs & extranet',
    items: [
      { q: "Formateur interne ou externe : quelle différence ?", a: "Un formateur interne fait partie de votre équipe (accès complet à /app selon son rôle). Un formateur externe (sous-traitant) accède uniquement à son espace extranet /trainer : ses sessions, émargements et évaluations." },
      { q: "Comment donner accès à l'extranet formateur ?", a: "Fiche formateur → Extranet → Activer l'accès. Le formateur reçoit une invitation par email et peut se connecter par mot de passe, Google ou Microsoft." },
      { q: "Comment gérer les contrats de sous-traitance ?", a: "Fiche formateur → Contrats : générez le contrat PDF, envoyez-le pour signature électronique. Le statut (envoyé, signé) est suivi automatiquement." },
    ],
  },
  {
    id: 'crm', icon: Briefcase, title: 'CRM & devis',
    items: [
      { q: "Comment fonctionne le pipeline CRM ?", a: "CRM / Prospects affiche vos opportunités en Kanban (Nouveau → Contacté → Négociation → Gagné/Perdu). Glissez-déposez les cartes pour changer d'étape ; chaque prospect garde l'historique des activités et emails." },
      { q: "Comment créer et envoyer un devis ?", a: "Devis → Nouveau : lignes de prestation, TVA, mentions. Le PDF est généré avec votre charte et envoyé pour signature électronique. À la signature, le devis peut être converti en session + convention + facture." },
      { q: "Comment suivre les relances ?", a: "Les devis envoyés sans réponse apparaissent dans le widget Pipeline du tableau de bord. Vous pouvez programmer des relances automatiques par email dans Paramètres → Emails → Automatisations." },
    ],
  },
  {
    id: 'factures', icon: Receipt, title: 'Factures & paiements',
    items: [
      { q: "Comment facturer une session ?", a: "Depuis la session ou le devis signé : Générer la facture. Numérotation automatique conforme, TVA paramétrable, échéances et acomptes gérés. Le PDF reprend vos mentions légales de Paramètres → Facturation." },
      { q: "Comment suivre les impayés ?", a: "Le widget « Factures impayées » et la page Factures filtrent par statut (brouillon, envoyée, payée, en retard). Des rappels automatiques peuvent être programmés avant/après échéance." },
      { q: "Comment gérer un avoir ?", a: "Sur une facture émise → menu actions → Créer un avoir. L'avoir reprend la numérotation et annule tout ou partie de la facture d'origine." },
      { q: "Financement OPCO : subrogation ou remboursement ?", a: "Sur la fiche OPCO (Paramètres → OPCO), le mode de paiement est indiqué : en subrogation, vous facturez directement l'OPCO ; en remboursement, vous facturez l'entreprise qui se fait rembourser. Le délai de dépôt (J-X avant formation) est également suivi." },
    ],
  },
  {
    id: 'emails', icon: Mail, title: 'Boîte email',
    items: [
      { q: "Comment connecter ma boîte email ?", a: "Paramètres → Emails : connectez Gmail (OAuth Google), Outlook (Microsoft) ou tout autre fournisseur via IMAP/SMTP. Les échanges sont ensuite rattachés automatiquement aux fiches entreprises et prospects." },
      { q: "Comment fonctionnent les modèles d'emails ?", a: "Paramètres → Emails → Modèles : créez des modèles avec variables ({{prenom}}, {{session}}, {{date}}…) pour les convocations, relances et confirmations. Ils sont proposés lors de chaque envoi." },
      { q: "Puis-je programmer un envoi ?", a: "Oui — dans le composeur, choisissez « Envoyer plus tard » et la date/heure. L'email part automatiquement (fuseau Europe/Paris)." },
      { q: "Comment mettre un fil en attente (snooze) ?", a: "Dans la boîte email, l'action Snooze masque le fil jusqu'à la date choisie, puis le fait remonter en haut de la liste." },
    ],
  },
  {
    id: 'qualiopi', icon: ShieldCheck, title: 'Qualiopi',
    items: [
      { q: "Comment OFManager m'aide pour Qualiopi ?", a: "Le module Qualiopi suit les 7 critères et 32 indicateurs : chaque indicateur liste les preuves attendues et celles déjà présentes dans votre espace (programmes, émargements, évaluations, réclamations…). Le score global est visible sur le tableau de bord." },
      { q: "Comment préparer un audit ?", a: "Qualiopi → Export audit : générez le dossier de preuves par indicateur. Vérifiez les indicateurs en rouge et complétez les documents manquants avant la date d'audit." },
      { q: "Les questionnaires de satisfaction sont-ils conformes ?", a: "Oui — les questionnaires à chaud/à froid envoyés automatiquement après chaque session alimentent l'indicateur 30. Les résultats sont agrégés dans Évaluations." },
    ],
  },
  {
    id: 'extranet', icon: Globe2, title: 'Extranet clients',
    items: [
      { q: "Qu'est-ce que l'extranet client ?", a: "Un portail dédié où vos entreprises clientes consultent leurs sessions, documents (conventions, factures, certificats) et questionnaires. Activez-le depuis Extranet clients et partagez le lien personnalisé." },
      { q: "Comment un client accède-t-il à ses documents ?", a: "Le client reçoit un lien sécurisé par email. Aucun mot de passe à retenir : l'accès est validé par un code envoyé à son adresse." },
    ],
  },
  {
    id: 'equipe', icon: Settings, title: 'Équipe, rôles & paramètres',
    items: [
      { q: "Comment inviter un collaborateur ?", a: "Paramètres → Équipe → Inviter : indiquez email et rôle (Administrateur, Gestionnaire, Formateur, Lecture seule…). L'invité reçoit un email pour créer son mot de passe ou se connecter via Google/Microsoft." },
      { q: "Que signifie le badge « X organismes » sur un membre ?", a: "Il indique que l'email de ce membre est rattaché à plusieurs organismes OFManager. À la connexion, il choisit son organisme ; ses données restent isolées par organisme." },
      { q: "Comment personnaliser les rôles ?", a: "Paramètres → Rôles & permissions : ajustez module par module ce que chaque rôle peut voir et faire (lecture, écriture, aucun accès). Les menus latéraux s'adaptent automatiquement." },
      { q: "Comment imposer une méthode de connexion (SSO) ?", a: "Paramètres → Sécurité → Politique d'authentification : autorisez/interdisez mot de passe, Google, Microsoft, et forcez une méthode par domaine email (ex. @cabinet.fr → Microsoft uniquement)." },
    ],
  },
  {
    id: 'securite', icon: Lock, title: 'Sécurité & données',
    items: [
      { q: "Où sont stockées mes données ?", a: "Vos données sont hébergées en France et isolées par organisme (multi-tenant strict) : aucun autre organisme ne peut accéder à vos informations." },
      { q: "Comment consulter le journal d'audit ?", a: "Journal d'audit (menu Plateforme) : chaque action sensible (connexion, modification, suppression, export) est tracée avec l'auteur, la date et l'adresse IP." },
      { q: "Que se passe-t-il après 5 tentatives de connexion échouées ?", a: "Le compte est verrouillé 15 minutes (protection force brute). Un administrateur peut le déverrouiller immédiatement depuis Paramètres → Équipe." },
      { q: "Comment déconnecter toutes mes sessions ?", a: "Mon compte → Sécurité → Sessions actives : consultez les appareils connectés et révoquez-les individuellement ou tous d'un coup. Changer de mot de passe déconnecte aussi toutes les sessions." },
    ],
  },
];

function Article({ item, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border border-[#EFF0F6] rounded-xl overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#FAFAFE] transition-colors"
        data-testid={`aide-q-${item.q.slice(0, 24).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="text-[13.5px] font-semibold text-[#1A1B2E]">{item.q}</span>
        <ChevronDown className={`w-4 h-4 text-[#8E8FA5] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-[13px] text-[#4A4B63] leading-relaxed border-t border-[#F3F4F8] pt-3">
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function Aide() {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS
      .map((s) => ({
        ...s,
        items: s.items.filter((it) => (it.q + ' ' + it.a).toLowerCase().includes(q)),
      }))
      .filter((s) => s.items.length > 0);
  }, [query]);

  const total = useMemo(() => SECTIONS.reduce((n, s) => n + s.items.length, 0), []);

  return (
    <div className="p-6 max-w-[980px] mx-auto" data-testid="aide-page">
      {/* Hero */}
      <div className="rounded-2xl p-8 mb-6 text-white" style={{ background: 'radial-gradient(at 20% 0%, #2A2356 0%, #14132E 60%, #0A0920 100%)' }}>
        <div className="flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-wider text-white/70 mb-2">
          <LifeBuoy className="w-4 h-4" /> Centre d'aide
        </div>
        <h1 className="text-[26px] font-bold leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Comment pouvons-nous vous aider ?
        </h1>
        <p className="text-[13.5px] text-white/70 mt-1">{total} articles couvrant tous les modules d'OFManager.</p>
        <div className="relative mt-5 max-w-[520px]">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input
            data-testid="aide-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher : émargement, OPCO, Qualiopi, facture…"
            className="w-full h-11 pl-10 pr-4 rounded-xl bg-white text-[#1A1B2E] text-[14px] outline-none focus:ring-2 focus:ring-[#7C5CFC]"
          />
        </div>
      </div>

      {/* Quick nav */}
      {!query && (
        <div className="flex flex-wrap gap-2 mb-6">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#aide-${s.id}`}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-[#F6F7FB] border border-[#EFF0F6] text-[12px] font-semibold text-[#4A4B63] hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors"
              data-testid={`aide-nav-${s.id}`}
            >
              <s.icon className="w-3.5 h-3.5" /> {s.title}
            </a>
          ))}
        </div>
      )}

      {/* Sections */}
      {filtered.length === 0 && (
        <div className="text-center py-16 text-[#8E8FA5]" data-testid="aide-empty">
          <Search className="w-8 h-8 mx-auto mb-3 opacity-40" />
          <p className="text-[14px]">Aucun article ne correspond à « {query} ».</p>
        </div>
      )}
      <div className="space-y-8">
        {filtered.map((s) => (
          <section key={s.id} id={`aide-${s.id}`} data-testid={`aide-section-${s.id}`}>
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 rounded-lg bg-[#EDE8FF] flex items-center justify-center">
                <s.icon className="w-4 h-4 text-[#4F46E5]" />
              </div>
              <h2 className="text-[16px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {s.title}
              </h2>
              <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-[#F3F4F8] text-[#8E8FA5]">{s.items.length}</span>
            </div>
            <div className="space-y-2">
              {s.items.map((it) => (
                <Article key={it.q} item={it} defaultOpen={!!query} />
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Footer contact */}
      <div className="mt-10 p-5 rounded-2xl bg-[#F6F7FB] border border-[#EFF0F6] flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex-1">
          <div className="text-[14px] font-bold text-[#1A1B2E]">Vous ne trouvez pas votre réponse ?</div>
          <div className="text-[12.5px] text-[#6B6C85] mt-0.5">Notre équipe support vous répond sous 24 h ouvrées.</div>
        </div>
        <a
          href="mailto:support@ofmanager.fr"
          data-testid="aide-contact-support"
          className="h-10 px-5 rounded-xl brand-gradient text-white text-[13px] font-semibold inline-flex items-center"
        >
          Contacter le support
        </a>
      </div>
    </div>
  );
}
